import { useEffect, useMemo, useState } from "react";
import {Bell, Plus, Trash2, Pencil, Check, X, Loader2, AlertCircle, Sparkles, Power, PowerOff, MapPin} from 'lucide-react';
import toast from "react-hot-toast";
import { useJobAlerts, type JobAlert, type AlertCategory, type AlertFrequency, type NewJobAlert } from "@/hooks/useJobAlerts";
import { FRENCH_REGIONS } from "@/hooks/useAvailableCandidates";
import { checkAndNotifyJobAlerts } from "@/lib/jobAlertsService";

const CATEGORY_OPTIONS: { value: AlertCategory; label: string; color: string }[] = [
  { value: "ALL", label: "Toutes (BNSSA + MNS)", color: "text-gray-700" },
  { value: "BNSSA", label: "BNSSA", color: "text-red-600" },
  { value: "MNS", label: "MNS", color: "text-blue-600" },
];

const FREQUENCY_OPTIONS: { value: AlertFrequency; label: string; description: string }[] = [
  { value: "immediate", label: "Immédiate", description: "Dès qu'une offre correspond" },
  { value: "daily", label: "Quotidienne", description: "Récap chaque jour" },
  { value: "weekly", label: "Hebdomadaire", description: "Récap chaque semaine" },
];

interface JobAlertsManagerProps {
  userId: string;
  userEmail?: string;
  /** Pré-remplissage optionnel (ex. depuis filtres /offres) */
  initialDraft?: Partial<NewJobAlert>;
}

export default function JobAlertsManager({ userId, userEmail, initialDraft }: JobAlertsManagerProps) {
  const { alerts, loading, error, fetchMine, createAlert, updateAlert, deleteAlert } = useJobAlerts();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state — uniquement Catégorie, Fréquence, Région
  const [name, setName] = useState("");
  const [category, setCategory] = useState<AlertCategory>("ALL");
  const [region, setRegion] = useState("");
  const [frequency, setFrequency] = useState<AlertFrequency>("weekly");

  useEffect(() => {
    if (userId) fetchMine(userId);
  }, [userId, fetchMine]);

  // Si un draft initial est fourni (depuis /offres), pré-ouvrir le formulaire
  useEffect(() => {
    if (initialDraft && Object.keys(initialDraft).length > 0) {
      setName(initialDraft.name || "Mon alerte personnalisée");
      setCategory(initialDraft.category || "ALL");
      setRegion(initialDraft.region || "");
      setFrequency(initialDraft.frequency || "weekly");
      setShowForm(true);
    }
  }, [initialDraft]);

  const resetForm = () => {
    setName("");
    setCategory("ALL");
    setRegion("");
    setFrequency("weekly");
    setEditingId(null);
    setShowForm(false);
  };

  const startEdit = (a: JobAlert) => {
    setEditingId(a._id);
    setName(a.name);
    setCategory(a.category);
    setRegion(a.region || "");
    setFrequency(a.frequency);
    setShowForm(true);
    setTimeout(() => {
      const el = document.getElementById("alert-form");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Donnez un nom à votre alerte.");
      return;
    }
    setSubmitting(true);
    try {
      const data: NewJobAlert = {
        name: name.trim(),
        category,
        region: region.trim() || undefined,
        frequency,
        isActive: true,
      };
      if (editingId) {
        await updateAlert(editingId, data);
        toast.success("Alerte mise à jour");
      } else {
        await createAlert(userId, userEmail, data);
        toast.success("Alerte créée — nous vérifions immédiatement les offres correspondantes…");
        // Scan immédiat : on envoie l'email tout de suite si des offres existantes
        // correspondent déjà à cette alerte (sans attendre une prochaine visite du
        // dashboard, et en forçant le bypass du throttle anti-spam).
        void checkAndNotifyJobAlerts({
          userId,
          userEmail,
          force: true,
        });
      }
      resetForm();
    } catch (err) {
      console.error(err);
      toast.error("Une erreur est survenue.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (a: JobAlert) => {
    try {
      await updateAlert(a._id, { isActive: !a.isActive });
      toast.success(a.isActive ? "Alerte mise en pause" : "Alerte réactivée");
    } catch {
      toast.error("Impossible de modifier l'alerte.");
    }
  };

  const handleDelete = async (a: JobAlert) => {
    if (!window.confirm(`Supprimer l'alerte « ${a.name} » ?`)) return;
    try {
      await deleteAlert(a._id);
      toast.success("Alerte supprimée");
    } catch {
      toast.error("Impossible de supprimer l'alerte.");
    }
  };

  const summary = useMemo(() => {
    return (a: JobAlert) => {
      const bits: string[] = [];
      bits.push(a.category === "ALL" ? "BNSSA + MNS" : a.category);
      bits.push(`📍 ${a.region?.trim() ? a.region : "Toutes régions"}`);
      const freq = FREQUENCY_OPTIONS.find((f) => f.value === a.frequency);
      bits.push(freq ? freq.label : a.frequency);
      return bits.join(" · ");
    };
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Bell className="w-5 h-5 text-amber-500" />
            Mes alertes emploi
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Recevez par email les nouvelles offres qui correspondent à vos critères.
          </p>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 bg-gradient-to-br from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Nouvelle alerte
          </button>
        )}
      </div>

      {/* Formulaire création / édition */}
      {showForm && (
        <form
          id="alert-form"
          onSubmit={handleSubmit}
          className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5 space-y-4"
        >
          <div className="flex items-center gap-2 text-sm font-semibold text-amber-800">
            <Sparkles className="w-4 h-4" />
            {editingId ? "Modifier l'alerte" : "Créer une nouvelle alerte"}
          </div>

          {/* Nom */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Nom de l'alerte *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex. MNS en Île-de-France"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Catégorie */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Catégorie
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as AlertCategory)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                {CATEGORY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            {/* Fréquence */}
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">
                Fréquence
              </label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value as AlertFrequency)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
              >
                {FREQUENCY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label} — {o.description}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Région */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" />
              Région
            </label>
            <select
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="">Toutes les régions</option>
              {FRENCH_REGIONS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <p className="text-[10px] text-gray-500 mt-1">
              Sélectionnez une région française pour filtrer les offres, ou laissez « Toutes ».
            </p>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              {editingId ? "Mettre à jour" : "Créer l'alerte"}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
            >
              <X className="w-4 h-4" />
              Annuler
            </button>
          </div>
        </form>
      )}

      {/* Erreur */}
      {error && (
        <div className="flex items-center gap-2 text-red-600 bg-red-50 border border-red-200 rounded-xl p-4 text-sm">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && alerts.length === 0 && (
        <div className="flex items-center gap-2 text-gray-500 py-6">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Chargement de vos alertes…</span>
        </div>
      )}

      {/* État vide */}
      {!loading && alerts.length === 0 && !showForm && (
        <div className="bg-gray-50 border border-dashed border-gray-300 rounded-xl p-10 text-center">
          <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-700 font-semibold">Aucune alerte pour le moment</p>
          <p className="text-xs text-gray-500 mt-1 max-w-md mx-auto">
            Créez une alerte pour recevoir par email les nouvelles offres BNSSA et MNS qui correspondent à votre catégorie et à votre région.
          </p>
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 mt-4 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            <Plus className="w-4 h-4" />
            Créer ma première alerte
          </button>
        </div>
      )}

      {/* Liste des alertes */}
      {alerts.length > 0 && (
        <div className="space-y-3">
          {alerts.map((a) => (
            <div
              key={a._id}
              className={`bg-white border rounded-xl p-4 transition-colors ${
                a.isActive ? "border-gray-200" : "border-gray-200 opacity-60"
              }`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900 truncate">{a.name}</h3>
                    {a.isActive ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-200 text-gray-600">
                        En pause
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 mt-1 break-words">{summary(a)}</p>
                  {a.lastSentAt && (
                    <p className="text-[11px] text-gray-400 mt-1">
                      Dernier envoi : {new Date(a.lastSentAt).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => handleToggleActive(a)}
                    title={a.isActive ? "Mettre en pause" : "Réactiver"}
                    className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                  >
                    {a.isActive ? <PowerOff className="w-4 h-4" /> : <Power className="w-4 h-4" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => startEdit(a)}
                    title="Modifier"
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(a)}
                    title="Supprimer"
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
